import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../_shared/services/auth.service';
import { TokenService } from '../_shared/services/token.service';
import { ToastrService } from 'ngx-toastr';
import { ErrorMessages } from '../_shared/constants/error-messages.constant';
import { SessionService } from '../_shared/services/session.service';
import { BlockUI, BlockUIModule, NgBlockUI } from 'ng-block-ui';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment';
import { UserSettingsService } from '../_shared/services/user-settings.service';
import { LanguageService } from '../_shared/services/language.service';

@Component({
  selector: 'app-authenticate',
  standalone: true,
  imports: [
    BlockUIModule
  ],
  templateUrl: './authenticate.component.html',
  styleUrl: './authenticate.component.scss'
})
export class AuthenticateComponent implements OnInit {
  @BlockUI() blockUI!: NgBlockUI;

  constructor(
    private authService: AuthService,
    private tokenService: TokenService,
    private sessionService: SessionService,
    private toastr: ToastrService,
    private userSettingsService: UserSettingsService,
    private languageService: LanguageService,
    private route: ActivatedRoute) {
  }

  async ngOnInit(): Promise<void> {
    this.blockUI.start('Authenticating...');
    const params = await firstValueFrom(this.route.queryParams);
    const fragment = await firstValueFrom(this.route.fragment);

    await this.refreshToken();
    await this.createSession();
    await this.addUserSettings();
    this.applyUserLanguage();
    await this.generateAuthDataAndRedirectToEagleOne();
  }

  private async createSession(): Promise<void> {
    try {
      const response = await firstValueFrom(this.sessionService.createSession());
      if (response.errorCode) {
        this.handleError({ error: response });
      }
    } catch (error: any) {
      this.handleError(error);
    }
  }

  private async refreshToken(): Promise<void> {
    try {
      const response = await firstValueFrom(this.tokenService.refreshToken());
      if (response && !response.errorCode && response.data) {
        this.authService.setUser(response.data);
      }
    } catch (error: any) {
      this.handleError(error);
    }
  }

  private async addUserSettings() {
    try {
      const response = await this.userSettingsService.getUserSettings();
      if (response.data) {
        this.authService.setUserSettings(response?.data)
      }
    } catch (error) {
      this.handleError(error)
    }
  }

  private applyUserLanguage(): void {
    this.languageService.applyStoredLanguage();
  }

  private async generateAuthDataAndRedirectToEagleOne(): Promise<void> {
    try {
      await firstValueFrom(this.tokenService.generateToken());
      const url = `${environment.eagleOneUrl}`;
      setTimeout(() => {
        window.location.href = url;
      }, 500);
    } catch (error: any) {
      this.toastr.error('Redirecting failed. Please contact support');
    }
  }

  private handleError(error: any): void {
    const message = ErrorMessages[error?.error?.errorCode] || 'An error occured while logging in. Please try again or contact support for assistance.'
    this.toastr.error(message);
  }
}
